import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { pb } from '../lib/pocketbase.js';
import { useAuth } from '../auth/AuthContext.jsx';
import PraxisFormModal from '../components/PraxisFormModal.jsx';
import TelefonLink from '../components/TelefonLink.jsx';
import AnrufAmpel from '../components/AnrufAmpel.jsx';
import { formatDatum, tageSeit } from '../lib/aktivitaet.js';
import {
  FACHRICHTUNG_OPTIONS,
  fachgebietAnzeige,
} from '../lib/fachrichtung.js';
import {
  PRAXIS_ART_OPTIONS,
  PRAXIS_ART_SHORT_LABEL,
  PRAXIS_STATUS_BADGE,
  PRAXIS_STATUS_LABEL,
  PRAXIS_STATUS_OPTIONS,
  WIRTSCHAFTSMODELL_OPTIONS,
  UNTERNEHMENSFORM_OPTIONS,
  VERSORGUNGSFORM_OPTIONS,
  VERBUND_OPTIONS,
  praxisAnzeige,
  SOFTWARE_OPTIONS,
  ABRECHNUNGSSTELLE_OPTIONS,
  fetchFilialenCount,
  PRAXIS_VERTRAG_PARTNER_OPTIONS,
  PRAXIS_VERTRAG_PARTNER_SHORT_LABEL,
  PRAXIS_VERTRAG_STATUS_OPTIONS,
  PRAXIS_VERTRAG_STATUS_SHORT_LABEL,
  PRAXIS_VERTRAG_STATUS_BADGE_PILL,
} from '../lib/praxis.js';
import { getRecentPraxisIds } from '../lib/recentPraxen.js';
import {
  buildPraxenFilter,
  searchPraxen,
  fetchPraxenByIds,
  fetchFilialen,
} from '../lib/praxenSearch.js';

const STATUS_FILTER_OPTIONS = [{ value: 'alle', label: 'Alle' }, ...PRAXIS_STATUS_OPTIONS];
const PRAXIS_ART_FILTER_OPTIONS = [
  { value: 'alle', label: 'Alle' },
  ...PRAXIS_ART_OPTIONS.map((opt) => ({
    value: opt.value,
    // In der Filterleiste reicht das Kurzlabel.
    label: PRAXIS_ART_SHORT_LABEL[opt.value] ?? opt.label,
  })),
];
const WIRTSCHAFTSMODELL_FILTER_OPTIONS = [
  { value: 'alle', label: 'Alle' },
  ...WIRTSCHAFTSMODELL_OPTIONS,
];

const KONTAKT_FILTER_OPTIONS = [
  { value: 'alle', label: 'Alle' },
  { value: '30', label: 'Kein Kontakt seit 30 Tagen' },
  { value: '60', label: 'Kein Kontakt seit 60 Tagen' },
  { value: '90', label: 'Kein Kontakt seit 90 Tagen' },
];

const UMSATZ_FILTER_OPTIONS = [
  { value: 'alle', label: 'Alle' },
  { value: 'mit', label: 'Mit Umsatzdaten' },
  { value: 'ohne', label: 'Ohne Umsatzdaten' },
];

// Bekannte Statuswerte aus dem PB-Schema. Alles andere — leerer String,
// 'unbekannt', null — soll keinen Badge erzeugen, statt "Unbekannt" zu zeigen.
const KNOWN_STATUS_VALUES = new Set(PRAXIS_STATUS_OPTIONS.map((opt) => opt.value));

const SEITEN_GROESSE_OPTIONS = [
  { value: 20, label: '20' },
  { value: 50, label: '50' },
  { value: 'alle', label: 'Alle' },
];

// Der Filter-State, den die Suche braucht — als flaches Objekt zusammengefasst,
// damit wir beim Klick auf "Suchen" einen Snapshot festhalten können (siehe
// appliedState). Änderungen an den Formularfeldern wirken erst beim nächsten
// Suchen, nicht sofort auf die Ergebnisliste.
function makeFilterState({
  search,
  statusFilter,
  praxisArtFilter,
  fachrichtungFilter,
  wirtschaftsmodellFilter,
  unternehmensformFilter,
  versorgungsformFilter,
  verbundFilter,
  softwareFilter,
  abrechnungsstelleFilter,
  umsatzFilter,
  kontaktFilter,
  vertragPartnerFilter,
  vertragStatusFilter,
}) {
  return {
    search,
    statusFilter,
    praxisArtFilter,
    fachrichtungFilter,
    wirtschaftsmodellFilter,
    unternehmensformFilter,
    versorgungsformFilter,
    verbundFilter,
    softwareFilter,
    abrechnungsstelleFilter,
    umsatzFilter,
    kontaktFilter,
    vertragPartnerFilter,
    vertragStatusFilter,
  };
}

function setsEqual(a, b) {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

// Gleichheit zweier Filter-Snapshots — für den "Änderungen noch nicht
// angewendet"-Hinweis (dirty).
function sameFilterState(a, b) {
  if (!a || !b) return false;
  return (
    a.search === b.search &&
    a.statusFilter === b.statusFilter &&
    a.praxisArtFilter === b.praxisArtFilter &&
    a.fachrichtungFilter === b.fachrichtungFilter &&
    a.wirtschaftsmodellFilter === b.wirtschaftsmodellFilter &&
    a.unternehmensformFilter === b.unternehmensformFilter &&
    a.versorgungsformFilter === b.versorgungsformFilter &&
    setsEqual(a.verbundFilter, b.verbundFilter) &&
    a.softwareFilter === b.softwareFilter &&
    a.abrechnungsstelleFilter === b.abrechnungsstelleFilter &&
    a.umsatzFilter === b.umsatzFilter &&
    a.kontaktFilter === b.kontaktFilter &&
    setsEqual(a.vertragPartnerFilter, b.vertragPartnerFilter) &&
    setsEqual(a.vertragStatusFilter, b.vertragStatusFilter)
  );
}

function StatusBadge({ status }) {
  if (!KNOWN_STATUS_VALUES.has(status)) return null;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${
        PRAXIS_STATUS_BADGE[status] ?? 'bg-slate-100 text-slate-700 ring-slate-200'
      }`}
    >
      {PRAXIS_STATUS_LABEL[status] ?? status}
    </span>
  );
}

// Kompaktes Vertrags-Tag neben dem Praxis-Namen: "AS · aktiv" / "RHD · versendet".
// Erscheint nur, wenn vertrag_partner gepflegt ist. Farbcode richtet sich nach
// dem Status (versendet=amber, aktiv=emerald, abgelehnt=rose, gekündigt=slate);
// fehlt der Status (Edge-Case bevor Validierung griff), fällt es auf slate zurück.
function VertragBadge({ praxis }) {
  if (!praxis.vertrag_partner) return null;
  const partner = PRAXIS_VERTRAG_PARTNER_SHORT_LABEL[praxis.vertrag_partner] ?? praxis.vertrag_partner;
  const status = praxis.vertrag_status;
  const statusLabel = status
    ? PRAXIS_VERTRAG_STATUS_SHORT_LABEL[status] ?? status
    : '—';
  const klassen = status
    ? PRAXIS_VERTRAG_STATUS_BADGE_PILL[status] ?? 'bg-slate-100 text-slate-700 ring-slate-200'
    : 'bg-slate-100 text-slate-700 ring-slate-200';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${klassen}`}
      title={`Factoring-Vertrag (${partner}) · ${statusLabel}`}
    >
      {partner} · {statusLabel}
    </span>
  );
}

// Kompakte Karte für den "Zuletzt besucht"-Einstieg: Name, Ort, Fachrichtung
// und ggf. Vertrag-Badge. Klickbar auf die Detailseite.
function RecentCard({ praxis }) {
  const fach = fachgebietAnzeige(praxis);
  const ort = [praxis.plz, praxis.ort].filter(Boolean).join(' ');
  return (
    <Link
      to={`/praxen/${praxis.id}`}
      className="block rounded-lg border border-slate-200 p-3 transition hover:border-primary-300 hover:bg-primary-50/40"
    >
      <div className="flex items-center gap-2">
        <AnrufAmpel praxis={praxis} size="sm" />
        <span className="truncate font-semibold text-slate-900">{praxisAnzeige(praxis)}</span>
        <VertragBadge praxis={praxis} />
      </div>
      {fach && <div className="mt-0.5 truncate text-sm text-slate-500">{fach}</div>}
      <div className="mt-1 text-sm text-slate-500">
        {ort || <span className="text-slate-400">—</span>}
      </div>
    </Link>
  );
}

// Sortier-Header analog zu SelektionDetailPage: klickbar, Pfeil-Indikator,
// Toggle asc/desc. "Letzter Kontakt" ist bewusst NICHT sortierbar (keine
// globale Sortierung auf dem nur pro Seite angereicherten Feld).
function SortPfeil({ active, dir }) {
  if (!active) return null;
  return <span className="ml-1 text-slate-400">{dir === 'asc' ? '▲' : '▼'}</span>;
}

function SortHeader({ children, sortKey, currentKey, currentDir, onSort }) {
  const active = sortKey === currentKey;
  return (
    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center hover:text-slate-700"
      >
        {children}
        <SortPfeil active={active} dir={currentDir} />
      </button>
    </th>
  );
}

// Tabellen-Zeile für eine Hauptpraxis ODER eine Filiale. Filialen werden
// optisch eingerückt und mit ↳-Indikator markiert.
function renderPraxisRow({
  praxis,
  istFiliale,
  hatFilialen,
  expanded,
  onToggleExpand,
  onEdit,
  onDelete,
  isAdmin,
}) {
  const fachAnzeige = fachgebietAnzeige(praxis);
  const tage = tageSeit(praxis.last_kontakt);
  const ort = [praxis.plz, praxis.ort].filter(Boolean).join(' ');
  const rowClass = istFiliale
    ? 'group bg-sky-50/40 hover:bg-sky-50'
    : 'group hover:bg-slate-50';
  return (
    <tr key={praxis.id} className={rowClass}>
      <td className={istFiliale ? 'py-3 pr-4 pl-12' : 'px-4 py-4'}>
        <div className="flex items-center gap-2">
          {!istFiliale && (
            <span className="inline-flex h-5 w-5 items-center justify-center">
              {hatFilialen ? (
                <button
                  type="button"
                  onClick={onToggleExpand}
                  className="rounded text-slate-500 hover:bg-slate-200 hover:text-slate-900"
                  aria-label={expanded ? 'Filialen einklappen' : 'Filialen ausklappen'}
                  title={expanded ? 'Filialen einklappen' : 'Filialen ausklappen'}
                >
                  {expanded ? '▼' : '▶'}
                </button>
              ) : null}
            </span>
          )}
          {istFiliale && (
            <span className="text-sky-600" aria-hidden="true">
              ↳
            </span>
          )}
          <AnrufAmpel praxis={praxis} size="sm" />
          <Link
            to={`/praxen/${praxis.id}`}
            className={`font-semibold hover:text-primary-700 hover:underline ${
              istFiliale ? 'text-sm text-slate-800' : 'text-base text-slate-900'
            }`}
          >
            {praxisAnzeige(praxis)}
          </Link>
          <VertragBadge praxis={praxis} />
        </div>
        {fachAnzeige && (
          <div className={`mt-0.5 text-sm text-slate-500 ${istFiliale ? 'ml-6' : ''}`}>
            {fachAnzeige}
          </div>
        )}
      </td>
      <td className={istFiliale ? 'py-3 pr-4' : 'px-4 py-4'}>
        <StatusBadge status={praxis.status} />
      </td>
      <td className={`text-sm text-slate-600 ${istFiliale ? 'py-3 pr-4' : 'px-4 py-4'}`}>
        {ort || <span className="text-slate-400">—</span>}
      </td>
      <td className={`text-sm ${istFiliale ? 'py-3 pr-4' : 'px-4 py-4'}`}>
        <TelefonLink phone={praxis.telefon} praxisId={praxis.id} />
      </td>
      <td className={`text-sm ${istFiliale ? 'py-3 pr-4' : 'px-4 py-4'}`}>
        {tage === null ? (
          <span className="text-slate-400">Noch kein Kontakt</span>
        ) : (
          <span className={tage > 30 ? 'font-medium text-red-600' : 'text-slate-700'}>
            {formatDatum(praxis.last_kontakt)}
          </span>
        )}
      </td>
      <td className={`text-right ${istFiliale ? 'py-3 pr-4' : 'px-4 py-4'}`}>
        <div className="flex justify-end gap-2 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
          <button
            type="button"
            className="text-sm font-medium text-primary-600 hover:text-primary-800"
            onClick={() => onEdit(praxis)}
          >
            Bearbeiten
          </button>
          {isAdmin && (
            <button
              type="button"
              className="text-sm font-medium text-red-600 hover:text-red-800"
              onClick={() => onDelete(praxis)}
            >
              Löschen
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function PraxenListPage() {
  const { isAdmin } = useAuth();

  // --- Filter-Formular (staged — wirkt erst beim Klick auf "Suchen") ---
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('alle');
  const [praxisArtFilter, setPraxisArtFilter] = useState('alle');
  const [fachrichtungFilter, setFachrichtungFilter] = useState('alle');
  const [kontaktFilter, setKontaktFilter] = useState('alle');
  const [umsatzFilter, setUmsatzFilter] = useState('alle');
  const [wirtschaftsmodellFilter, setWirtschaftsmodellFilter] = useState('alle');
  const [unternehmensformFilter, setUnternehmensformFilter] = useState('alle');
  const [versorgungsformFilter, setVersorgungsformFilter] = useState('alle');
  // Multi-Select wie die Vertrags-Filter: leeres Set = kein Filter aktiv.
  const [verbundFilter, setVerbundFilter] = useState(() => new Set());
  const [softwareFilter, setSoftwareFilter] = useState('alle');
  const [abrechnungsstelleFilter, setAbrechnungsstelleFilter] = useState('alle');
  // Vertrags-Filter: Multi-Select. Leeres Set bedeutet "kein Filter aktiv".
  // Vertrag-Filter erlaubt zusätzlich den Sentinel-Wert 'keiner' für Praxen
  // ohne erfassten vertrag_partner.
  const [vertragPartnerFilter, setVertragPartnerFilter] = useState(() => new Set());
  const [vertragStatusFilter, setVertragStatusFilter] = useState(() => new Set());
  const [filtersOpen, setFiltersOpen] = useState(false);

  // --- On-Demand-Suche ---
  // hasSearched=false zeigt den "Zuletzt besucht"-Einstieg. Erst "Suchen"
  // (Button oder Enter) lädt Ergebnisse. appliedState ist der Snapshot der
  // Filter, der die aktuellen Ergebnisse erzeugt hat — Pagination/Sort laden
  // damit nach, nicht mit zwischenzeitlich geänderten (noch nicht gesuchten)
  // Formularwerten.
  const [hasSearched, setHasSearched] = useState(false);
  const [appliedState, setAppliedState] = useState(null);
  const [results, setResults] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [resultsError, setResultsError] = useState(null);
  const [reloadNonce, setReloadNonce] = useState(0);

  // --- Zuletzt besucht ---
  const [recentPraxen, setRecentPraxen] = useState([]);
  const [recentLoading, setRecentLoading] = useState(true);

  // --- Status-Zähler (nicht-blockierend nachgeladen) ---
  const [statusCounts, setStatusCounts] = useState({});

  // --- Pagination + Sortierung ---
  const [seitenGroesse, setSeitenGroesse] = useState(20);
  const [seite, setSeite] = useState(1);
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  // --- Filialen-Aufklapp (manuell) ---
  // expandedMutterIds: manuell aufgeklappte Mütter (zeigen ALLE Filialen).
  // Auto-Aufklappen bei Text-Treffer läuft über result._matchingFilialen und
  // mutiert diesen State nicht.
  const [expandedMutterIds, setExpandedMutterIds] = useState(() => new Set());
  // Cache mutterId → { loading, items } für manuell nachgeladene Filialen.
  const [filialenCache, setFilialenCache] = useState(() => new Map());

  // --- Modal ---
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const aktiveFilterAnzahl =
    (statusFilter !== 'alle' ? 1 : 0) +
    (praxisArtFilter !== 'alle' ? 1 : 0) +
    (fachrichtungFilter !== 'alle' ? 1 : 0) +
    (kontaktFilter !== 'alle' ? 1 : 0) +
    (umsatzFilter !== 'alle' ? 1 : 0) +
    (wirtschaftsmodellFilter !== 'alle' ? 1 : 0) +
    (unternehmensformFilter !== 'alle' ? 1 : 0) +
    (versorgungsformFilter !== 'alle' ? 1 : 0) +
    (verbundFilter.size > 0 ? 1 : 0) +
    (softwareFilter !== 'alle' ? 1 : 0) +
    (abrechnungsstelleFilter !== 'alle' ? 1 : 0) +
    (vertragPartnerFilter.size > 0 ? 1 : 0) +
    (vertragStatusFilter.size > 0 ? 1 : 0);
  const filtersExpanded = filtersOpen || aktiveFilterAnzahl > 0;

  const currentFormState = useMemo(
    () =>
      makeFilterState({
        search,
        statusFilter,
        praxisArtFilter,
        fachrichtungFilter,
        wirtschaftsmodellFilter,
        unternehmensformFilter,
        versorgungsformFilter,
        verbundFilter,
        softwareFilter,
        abrechnungsstelleFilter,
        umsatzFilter,
        kontaktFilter,
        vertragPartnerFilter,
        vertragStatusFilter,
      }),
    [
      search,
      statusFilter,
      praxisArtFilter,
      fachrichtungFilter,
      wirtschaftsmodellFilter,
      unternehmensformFilter,
      versorgungsformFilter,
      verbundFilter,
      softwareFilter,
      abrechnungsstelleFilter,
      umsatzFilter,
      kontaktFilter,
      vertragPartnerFilter,
      vertragStatusFilter,
    ]
  );

  // "Änderungen noch nicht angewendet": nur relevant, wenn schon gesucht wurde.
  const dirty = hasSearched && !sameFilterState(currentFormState, appliedState);

  const sort = `${sortDir === 'desc' ? '-' : ''}${sortKey}`;

  // Zuletzt besucht laden (nur IDs → per getList nachladen, Reihenfolge erhalten).
  const reloadRecent = useCallback(() => {
    const ids = getRecentPraxisIds();
    if (ids.length === 0) {
      setRecentPraxen([]);
      return Promise.resolve();
    }
    return fetchPraxenByIds(pb, ids)
      .then((list) => setRecentPraxen(list))
      .catch(() => {
        /* still ignorieren — Einstieg bleibt dann leer */
      });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setRecentLoading(true);
    reloadRecent().finally(() => {
      if (!cancelled) setRecentLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [reloadRecent]);

  // Status-Zähler nicht-blockierend nachladen: pro Status ein winziger
  // getList(perPage=1)-Query (nur totalItems). Fehler still fallen lassen.
  useEffect(() => {
    let cancelled = false;
    for (const opt of STATUS_FILTER_OPTIONS) {
      const filter = buildPraxenFilter(pb, { statusFilter: opt.value });
      pb.collection('praxen')
        // Eigener requestKey je Status — sonst canceln sich die 6 parallelen
        // Zähl-Queries gegenseitig per PB-Auto-Cancel (nur der letzte bliebe).
        .getList(1, 1, { filter, fields: 'id', requestKey: `praxen-count-${opt.value}` })
        .then((res) => {
          if (!cancelled) {
            setStatusCounts((prev) => ({ ...prev, [opt.value]: res.totalItems }));
          }
        })
        .catch(() => {});
    }
    return () => {
      cancelled = true;
    };
  }, []);

  // Ergebnisse laden, sobald appliedState / Seite / Seitengröße / Sortierung
  // sich ändern (oder ein Reload angestoßen wird). Nur wenn schon gesucht wurde.
  useEffect(() => {
    if (!hasSearched || !appliedState) return undefined;
    let cancelled = false;
    setResultsLoading(true);
    setResultsError(null);
    searchPraxen(pb, { state: appliedState, page: seite, perPage: seitenGroesse, sort })
      .then((res) => {
        if (!cancelled) setResults(res);
      })
      .catch((err) => {
        if (!cancelled) setResultsError(err?.message || 'Suche fehlgeschlagen.');
      })
      .finally(() => {
        if (!cancelled) setResultsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hasSearched, appliedState, seite, seitenGroesse, sort, reloadNonce]);

  function resetFilialen() {
    setExpandedMutterIds(new Set());
    setFilialenCache(new Map());
  }

  function handleSearch() {
    // Snapshot der aktuellen Formularwerte festhalten und Suche auslösen.
    setAppliedState(makeFilterState(currentFormState));
    setSeite(1);
    setHasSearched(true);
    resetFilialen();
  }

  function handleSearchKeyDown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearch();
    }
  }

  function handleReset() {
    setSearch('');
    setStatusFilter('alle');
    setPraxisArtFilter('alle');
    setFachrichtungFilter('alle');
    setKontaktFilter('alle');
    setUmsatzFilter('alle');
    setWirtschaftsmodellFilter('alle');
    setUnternehmensformFilter('alle');
    setVersorgungsformFilter('alle');
    setVerbundFilter(new Set());
    setSoftwareFilter('alle');
    setAbrechnungsstelleFilter('alle');
    setVertragPartnerFilter(new Set());
    setVertragStatusFilter(new Set());
    setFiltersOpen(false);
    setSortKey('name');
    setSortDir('asc');
    setSeite(1);
    setHasSearched(false);
    setAppliedState(null);
    setResults(null);
    setResultsError(null);
    resetFilialen();
  }

  // Manuell aufgeklappte Filialen neu laden (nach Edit/Delete), ohne den
  // Aufklapp-Zustand zu verlieren — so werden Änderungen an Filialen sichtbar.
  function refetchExpandedFilialen() {
    if (expandedMutterIds.size === 0) return;
    setFilialenCache(() => {
      const frisch = new Map();
      for (const id of expandedMutterIds) frisch.set(id, { loading: true, items: [] });
      return frisch;
    });
    for (const id of expandedMutterIds) {
      fetchFilialen(pb, id)
        .then((items) => {
          setFilialenCache((cur) => {
            const m = new Map(cur);
            m.set(id, { loading: false, items });
            return m;
          });
        })
        .catch(() => {
          setFilialenCache((cur) => {
            const m = new Map(cur);
            m.set(id, { loading: false, items: [] });
            return m;
          });
        });
    }
  }

  // Cache-Invalidierung: Ergebnisseite neu laden (Mutter-Zeilen +
  // Text-Auto-Filialen kommen frisch aus searchPraxen), aufgeklappte
  // Filialen ebenfalls neu ziehen.
  function reloadResults() {
    setReloadNonce((n) => n + 1);
    refetchExpandedFilialen();
  }

  // Manuelles Aufklappen: Filialen der Mutter lazy nachladen (alle Filialen,
  // unabhängig von Filtern) und cachen. Erneuter Klick klappt ein.
  function toggleExpand(mutterId) {
    setExpandedMutterIds((prev) => {
      const next = new Set(prev);
      if (next.has(mutterId)) {
        next.delete(mutterId);
        return next;
      }
      next.add(mutterId);
      return next;
    });
    setFilialenCache((prev) => {
      if (prev.has(mutterId)) return prev;
      const next = new Map(prev);
      next.set(mutterId, { loading: true, items: [] });
      fetchFilialen(pb, mutterId)
        .then((items) => {
          setFilialenCache((cur) => {
            const m = new Map(cur);
            m.set(mutterId, { loading: false, items });
            return m;
          });
        })
        .catch(() => {
          setFilialenCache((cur) => {
            const m = new Map(cur);
            m.set(mutterId, { loading: false, items: [] });
            return m;
          });
        });
      return next;
    });
  }

  // Spalten-Sortierung: gleiche Spalte → Richtung togglen, neue Spalte → asc.
  // Zurück auf Seite 1, damit man nicht in der Mitte der Ergebnisse hängt.
  function handleSort(key) {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setSeite(1);
  }

  // Generischer Set-Toggle für die Vertrags-Filter-Pills.
  function toggleInSet(setter) {
    return (value) =>
      setter((prev) => {
        const next = new Set(prev);
        if (next.has(value)) next.delete(value);
        else next.add(value);
        return next;
      });
  }
  const toggleVerbund = toggleInSet(setVerbundFilter);
  const toggleVertragPartner = toggleInSet(setVertragPartnerFilter);
  const toggleVertragStatus = toggleInSet(setVertragStatusFilter);

  function openCreate() {
    setEditTarget(null);
    setModalOpen(true);
  }

  function openEdit(praxis) {
    setEditTarget(praxis);
    setModalOpen(true);
  }

  async function handleDelete(praxis) {
    if (!isAdmin) {
      alert('Du hast keine Berechtigung, eine Praxis zu löschen.');
      return;
    }
    // Hauptpraxis mit Filialen: hartes Veto. Der User soll erst die Filialen
    // abhängen oder selber löschen, bevor die Hauptpraxis verschwindet.
    const filialenCount = await fetchFilialenCount(pb, praxis.id);
    if (filialenCount > 0) {
      alert(
        `Diese Praxis hat ${filialenCount} ${filialenCount === 1 ? 'Filiale' : 'Filialen'}. ` +
          `Bitte erst die Filialen entfernen oder von der Mutter trennen.`
      );
      return;
    }
    if (
      !window.confirm(
        `"${praxis.name}" wirklich löschen? Alle zugeordneten Kontakte und Aktivitäten werden ebenfalls entfernt.`
      )
    ) {
      return;
    }
    try {
      await pb.collection('praxen').delete(praxis.id);
      reloadResults();
      reloadRecent();
    } catch (err) {
      if (err?.status === 403) {
        alert('Du hast keine Berechtigung, diesen Datensatz zu löschen.');
      } else {
        alert(err?.message || 'Löschen fehlgeschlagen.');
      }
    }
  }

  // --- Pagination-Kennzahlen aus dem Ergebnis ---
  const istAlle = seitenGroesse === 'alle';
  const totalItems = results?.totalItems ?? 0;
  const seitenAnzahl = results?.totalPages ?? 1;
  const aktuelleSeite = Math.min(seite, seitenAnzahl);
  const von = totalItems === 0 ? 0 : istAlle ? 1 : (aktuelleSeite - 1) * seitenGroesse + 1;
  const bis = istAlle ? totalItems : Math.min(aktuelleSeite * seitenGroesse, totalItems);

  const items = results?.items ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Praxen &amp; Kliniken</h1>
          <p className="mt-1 text-sm text-slate-600">
            Verwalte deine Arztpraxen und Klinik-Accounts.
          </p>
        </div>
        <button type="button" className="btn-primary" onClick={openCreate}>
          + Neue Praxis
        </button>
      </div>

      {/* Such- und Filter-Formular */}
      <div className="card space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="search"
            placeholder="Suche nach Name, Adresse, Telefon oder Kontaktperson …"
            className="input min-w-[16rem] flex-1"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
          <button
            type="button"
            className={`btn-primary ${dirty ? 'ring-2 ring-amber-400 ring-offset-1' : ''}`}
            onClick={handleSearch}
          >
            Suchen
          </button>
        </div>

        {dirty && (
          <p className="text-xs font-medium text-amber-600">
            Änderungen noch nicht angewendet — „Suchen" klicken zum Aktualisieren.
          </p>
        )}

        <button
          type="button"
          onClick={() => setFiltersOpen((v) => !v)}
          disabled={aktiveFilterAnzahl > 0}
          className="flex w-full items-center justify-between rounded-md px-2 py-1 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-default disabled:hover:bg-transparent"
        >
          <span className="inline-flex items-center gap-2">
            {aktiveFilterAnzahl > 0 ? (
              <>
                Filter
                <span className="inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-primary-600 px-1.5 py-0.5 text-xs font-semibold text-white">
                  {aktiveFilterAnzahl}
                </span>
              </>
            ) : (
              <>{filtersExpanded ? 'Filter ausblenden' : 'Filter anzeigen'}</>
            )}
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className={`h-4 w-4 transition-transform duration-200 ${
              filtersExpanded ? 'rotate-180' : ''
            }`}
          >
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        {filtersExpanded && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Status:
              </span>
              {STATUS_FILTER_OPTIONS.map((opt) => {
                const isActive = statusFilter === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setStatusFilter(opt.value)}
                    className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium transition ${
                      isActive
                        ? 'bg-primary-600 text-white'
                        : 'bg-primary-50 text-primary-700 hover:bg-primary-100'
                    }`}
                  >
                    {opt.label}
                    <span
                      className={`inline-flex min-w-[1.5rem] items-center justify-center rounded-full px-1.5 text-xs ${
                        isActive ? 'bg-white/20' : 'bg-white text-primary-700'
                      }`}
                    >
                      {statusCounts[opt.value] ?? '…'}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Praxis-Art:
              </span>
              {PRAXIS_ART_FILTER_OPTIONS.map((opt) => {
                const isActive = praxisArtFilter === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setPraxisArtFilter(opt.value)}
                    className={`inline-flex items-center rounded-full px-4 py-1.5 text-sm font-medium transition ${
                      isActive
                        ? 'bg-primary-600 text-white'
                        : 'bg-primary-50 text-primary-700 hover:bg-primary-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Kontakt:
              </span>
              {KONTAKT_FILTER_OPTIONS.map((opt) => {
                const isActive = kontaktFilter === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setKontaktFilter(opt.value)}
                    className={`inline-flex items-center rounded-full px-4 py-1.5 text-sm font-medium transition ${
                      isActive
                        ? 'bg-primary-600 text-white'
                        : 'bg-primary-50 text-primary-700 hover:bg-primary-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Umsatz:
              </span>
              {UMSATZ_FILTER_OPTIONS.map((opt) => {
                const isActive = umsatzFilter === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setUmsatzFilter(opt.value)}
                    className={`inline-flex items-center rounded-full px-4 py-1.5 text-sm font-medium transition ${
                      isActive
                        ? 'bg-primary-600 text-white'
                        : 'bg-primary-50 text-primary-700 hover:bg-primary-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Wirtschaftsmodell:
              </span>
              {WIRTSCHAFTSMODELL_FILTER_OPTIONS.map((opt) => {
                const isActive = wirtschaftsmodellFilter === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setWirtschaftsmodellFilter(opt.value)}
                    className={`inline-flex items-center rounded-full px-4 py-1.5 text-sm font-medium transition ${
                      isActive
                        ? 'bg-primary-600 text-white'
                        : 'bg-primary-50 text-primary-700 hover:bg-primary-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <label
                htmlFor="versorgungsform-filter"
                className="mr-1 text-xs font-semibold uppercase tracking-wider text-slate-500"
              >
                Versorgungsform:
              </label>
              <select
                id="versorgungsform-filter"
                className="input py-1.5"
                value={versorgungsformFilter}
                onChange={(e) => setVersorgungsformFilter(e.target.value)}
              >
                <option value="alle">Alle</option>
                {VERSORGUNGSFORM_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <label
                htmlFor="unternehmensform-filter"
                className="mr-1 text-xs font-semibold uppercase tracking-wider text-slate-500"
              >
                Unternehmensform:
              </label>
              <select
                id="unternehmensform-filter"
                className="input py-1.5"
                value={unternehmensformFilter}
                onChange={(e) => setUnternehmensformFilter(e.target.value)}
              >
                <option value="alle">Alle</option>
                {UNTERNEHMENSFORM_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <label
                htmlFor="fachrichtung-filter"
                className="mr-1 text-xs font-semibold uppercase tracking-wider text-slate-500"
              >
                Fachrichtung:
              </label>
              <select
                id="fachrichtung-filter"
                className="input py-1.5"
                value={fachrichtungFilter}
                onChange={(e) => setFachrichtungFilter(e.target.value)}
              >
                <option value="alle">Alle</option>
                {FACHRICHTUNG_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <label
                htmlFor="software-filter"
                className="mr-1 text-xs font-semibold uppercase tracking-wider text-slate-500"
              >
                Software:
              </label>
              <select
                id="software-filter"
                className="input py-1.5"
                value={softwareFilter}
                onChange={(e) => setSoftwareFilter(e.target.value)}
              >
                <option value="alle">Alle</option>
                {SOFTWARE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <label
                htmlFor="abrechnungsstelle-filter"
                className="mr-1 text-xs font-semibold uppercase tracking-wider text-slate-500"
              >
                Abrechnungsstelle:
              </label>
              <select
                id="abrechnungsstelle-filter"
                className="input py-1.5"
                value={abrechnungsstelleFilter}
                onChange={(e) => setAbrechnungsstelleFilter(e.target.value)}
              >
                <option value="alle">Alle</option>
                {ABRECHNUNGSSTELLE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Verbund:
              </span>
              {[{ value: 'keiner', label: 'Kein Verbund' }, ...VERBUND_OPTIONS].map((opt) => {
                const isActive = verbundFilter.has(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => toggleVerbund(opt.value)}
                    className={`inline-flex items-center rounded-full px-4 py-1.5 text-sm font-medium transition ${
                      isActive
                        ? 'bg-primary-600 text-white'
                        : 'bg-primary-50 text-primary-700 hover:bg-primary-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Vertrag:
              </span>
              {[
                ...PRAXIS_VERTRAG_PARTNER_OPTIONS,
                { value: 'keiner', label: 'Kein Vertrag' },
              ].map((opt) => {
                const isActive = vertragPartnerFilter.has(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => toggleVertragPartner(opt.value)}
                    className={`inline-flex items-center rounded-full px-4 py-1.5 text-sm font-medium transition ${
                      isActive
                        ? 'bg-primary-600 text-white'
                        : 'bg-primary-50 text-primary-700 hover:bg-primary-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                Vertragsstatus:
              </span>
              {PRAXIS_VERTRAG_STATUS_OPTIONS.map((opt) => {
                const isActive = vertragStatusFilter.has(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => toggleVertragStatus(opt.value)}
                    className={`inline-flex items-center rounded-full px-4 py-1.5 text-sm font-medium transition ${
                      isActive
                        ? 'bg-primary-600 text-white'
                        : 'bg-primary-50 text-primary-700 hover:bg-primary-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Einstieg: Zuletzt besucht — ODER Ergebnisliste nach dem Suchen */}
      {!hasSearched ? (
        <div className="card space-y-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Zuletzt besucht
          </div>
          {recentLoading ? (
            <div className="text-sm text-slate-500">Lade …</div>
          ) : recentPraxen.length === 0 ? (
            <p className="text-sm text-slate-500">
              Öffne eine Praxis oder nutze die Suche oben.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {recentPraxen.map((p) => (
                <RecentCard key={p.id} praxis={p} />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="card space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              onClick={handleReset}
              className="text-sm font-medium text-primary-700 hover:underline"
            >
              ← Zurück zur Übersicht
            </button>
          </div>

          {resultsError && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200">
              {resultsError}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <SortHeader
                    sortKey="name"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  >
                    Name / Fachgebiet
                  </SortHeader>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Status
                  </th>
                  <SortHeader
                    sortKey="plz"
                    currentKey={sortKey}
                    currentDir={sortDir}
                    onSort={handleSort}
                  >
                    Ort
                  </SortHeader>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Telefon
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Letzter Kontakt
                  </th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {resultsLoading && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-sm text-slate-500">
                      Lade …
                    </td>
                  </tr>
                )}
                {!resultsLoading && items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-sm text-slate-500">
                      Keine Einträge gefunden.
                    </td>
                  </tr>
                )}
                {!resultsLoading &&
                  items.flatMap((mutter) => {
                    const hatFilialen = (mutter._filialenCount ?? 0) > 0;
                    const manualExp = expandedMutterIds.has(mutter.id);
                    const matching = mutter._matchingFilialen;
                    const textAuto = Array.isArray(matching) && matching.length > 0;
                    const expanded = manualExp || textAuto;

                    const rows = [
                      renderPraxisRow({
                        praxis: mutter,
                        istFiliale: false,
                        hatFilialen,
                        expanded,
                        onToggleExpand: hatFilialen ? () => toggleExpand(mutter.id) : null,
                        onEdit: openEdit,
                        onDelete: handleDelete,
                        isAdmin,
                      }),
                    ];

                    if (expanded) {
                      // Manuell aufgeklappt → ALLE Filialen (lazy geladen).
                      // Sonst (nur Text-Treffer) → die matchenden Filialen.
                      if (manualExp) {
                        const cacheEntry = filialenCache.get(mutter.id);
                        if (cacheEntry?.loading) {
                          rows.push(
                            <tr key={`${mutter.id}-loading`} className="bg-sky-50/40">
                              <td colSpan={6} className="py-2 pl-12 pr-4 text-sm text-slate-500">
                                Filialen werden geladen …
                              </td>
                            </tr>
                          );
                        } else {
                          for (const f of cacheEntry?.items ?? []) {
                            rows.push(
                              renderPraxisRow({
                                praxis: f,
                                istFiliale: true,
                                hatFilialen: false,
                                expanded: false,
                                onToggleExpand: null,
                                onEdit: openEdit,
                                onDelete: handleDelete,
                                isAdmin,
                              })
                            );
                          }
                        }
                      } else {
                        for (const f of matching) {
                          rows.push(
                            renderPraxisRow({
                              praxis: f,
                              istFiliale: true,
                              hatFilialen: false,
                              expanded: false,
                              onToggleExpand: null,
                              onEdit: openEdit,
                              onDelete: handleDelete,
                              isAdmin,
                            })
                          );
                        }
                      }
                    }
                    return rows;
                  })}
              </tbody>
            </table>
          </div>

          {!resultsLoading && totalItems > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-3 text-sm">
              <div className="text-slate-600">
                {von}–{bis} von {totalItems} Hauptpraxen
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs uppercase tracking-wider text-slate-500">
                    Pro Seite:
                  </span>
                  {SEITEN_GROESSE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`rounded-md px-2.5 py-1 text-xs font-medium ring-1 ring-inset transition ${
                        seitenGroesse === opt.value
                          ? 'bg-primary-600 text-white ring-primary-600'
                          : 'bg-white text-slate-600 ring-slate-300 hover:bg-slate-50'
                      }`}
                      onClick={() => {
                        setSeitenGroesse(opt.value);
                        setSeite(1);
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {!istAlle && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="rounded-md px-3 py-1 text-sm font-medium text-slate-600 ring-1 ring-inset ring-slate-300 transition hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent"
                      onClick={() => setSeite((s) => Math.max(1, s - 1))}
                      disabled={aktuelleSeite <= 1}
                    >
                      ← Zurück
                    </button>
                    <span className="text-xs text-slate-500">
                      Seite {aktuelleSeite} / {seitenAnzahl}
                    </span>
                    <button
                      type="button"
                      className="rounded-md px-3 py-1 text-sm font-medium text-slate-600 ring-1 ring-inset ring-slate-300 transition hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent"
                      onClick={() => setSeite((s) => Math.min(seitenAnzahl, s + 1))}
                      disabled={aktuelleSeite >= seitenAnzahl}
                    >
                      Vor →
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <PraxisFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        praxis={editTarget}
        onSaved={() => {
          reloadResults();
          reloadRecent();
        }}
      />
    </div>
  );
}
